import React, { useRef, useState, useEffect } from "react";
import { Box, Typography, Grid, Button, Checkbox, FormControlLabel, CircularProgress, Alert } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { useReactToPrint } from "react-to-print";
import axios from "axios";
import config from "../config/Config";
import { getAppliedJobDetails } from "../helper/My_Helper";

const UndertakeForm = ({ candidateData = {} }) => {
  const formRef = useRef();
  const [isAgreed, setIsAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: "" });
  const [hasAgreedPreviously, setHasAgreedPreviously] = useState(false);

  const undertakeData = candidateData?.under_take_data || {};
const appliedJobDetails = getAppliedJobDetails(candidateData)
  useEffect(() => {
    const agreed = !!candidateData?.under_take_form_status;
  
    setHasAgreedPreviously(agreed);
  
    if (agreed) {
      setIsAgreed(true);
    }
  }, [candidateData]);

  const handlePrint = useReactToPrint({
    contentRef: formRef,
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm;
      }

      @media print {
        html, body {
          width: 210mm;
          height: 297mm;
        }

        .no-print {
          display: none !important;
        }

        .footer-section {
          page-break-inside: avoid;
        }

        * {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
  });

  const handleAgreementChange = async (event) => {
    const checked = event.target.checked;
    setIsAgreed(checked);

    if (checked) {
      // Submit agreement to API
      await submitAgreement();
    }
  };

  const submitAgreement = async () => {
    // Get the candidate ID from props
    const id = candidateData?._id;
    
    if (!id) {
      setSubmitStatus({
        success: false,
        message: "Candidate ID is required"
      });
      setIsAgreed(false);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: "" });

    try {
      const token = localStorage.getItem("token");
      
      const response = await axios.post(
        `${config.API_URL}agreeUndertakeForm/${id}`,
        {}, // Empty body as we're passing candidate_id in URL
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.status) {
        setSubmitStatus({
          success: true,
          message: response.data.message || "Agreement submitted successfully"
        });
        setIsAgreed(true);
        setHasAgreedPreviously(true);
      } else {
        setSubmitStatus({
          success: false,
          message: response.data.message || "Failed to submit agreement"
        });
        setIsAgreed(false);
      }
    } catch (error) {
      console.error("Error submitting agreement:", error);
      setSubmitStatus({
        success: false,
        message: error?.response?.data?.message || error.message || "Failed to submit agreement"
      });
      setIsAgreed(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrintClick = () => {
    if (isAgreed || hasAgreedPreviously) {
      handlePrint();
    }
  };

  const getCandidateName = () => {
    const fullName = candidateData?.applicant_form_data?.full_name;

    if (typeof fullName === "object") {
      return [
        fullName?.first_name,
        fullName?.middle_name,
        fullName?.surname,
      ]
        .filter(Boolean)
        .join(" ");
    }

    return (
      candidateData?.name ||
      candidateData?.applicant_form_data?.candidate_name ||
      ""
    );
  };

  const getFatherName = () => {
    return (
      candidateData?.applicant_form_data?.father_hushband_name ||
      candidateData?.applicant_form_data?.father_name ||
      ""
    );
  };

  const getAddress = () => {
    return (
      candidateData?.applicant_form_data?.permanent_address?.address ||
      candidateData?.applicant_form_data?.communication_address?.address ||
      ""
    );
  };

  const getDesignation = () => {
    return (
      candidateData?.job_title ||
      candidateData?.designation ||
      candidateData?.applied_jobs?.[0]?.job_designation ||
      ""
    );
  };

  const getYear = () => {
    if (undertakeData?.year) return undertakeData.year;

    if (undertakeData?.date) {
      const d = new Date(undertakeData.date);
      if (!isNaN(d.getTime())) return d.getFullYear();
    }

    return "____";
  };

  const getMonth = () => {
    if (undertakeData?.month) return undertakeData.month;

    if (undertakeData?.date) {
      const d = new Date(undertakeData.date);
      if (!isNaN(d.getTime())) {
        return d.toLocaleString("default", { month: "long" });
      }
    }

    return "____";
  };

  const getDay = () => {
    if (undertakeData?.date) {
      const d = new Date(undertakeData.date);
      if (!isNaN(d.getTime())) return d.getDate();
    }

    return "____";
  };

  const getCriminalRecord = () => {
    const value = undertakeData?.criminalRecord;

    if (value === "yes" || value === true) return "do";
    if (value === "no" || value === false) return "do not";

    return "do / do not";
  };

  const formatDate = (date) => {
    if (!date) return "";

    try {
      return new Date(date).toLocaleDateString("en-IN");
    } catch {
      return "";
    }
  };

  // Determine if print should be enabled
  const canPrint = isAgreed || hasAgreedPreviously;

  return (
    <Box>
      <Box sx={{ textAlign: "center", mb: 2 }} className="no-print">
        {!hasAgreedPreviously ? (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAgreed}
                  onChange={handleAgreementChange}
                  color="primary"
                  disabled={isSubmitting}
                />
              }
              label="I agree to the terms and conditions stated in this undertaking"
              sx={{ mb: 2, display: 'block' }}
            />
            
            {isSubmitting && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 1 }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography variant="body2">Submitting agreement...</Typography>
              </Box>
            )}

            {submitStatus.message && (
              <Alert 
                severity={submitStatus.success ? "success" : "error"} 
                sx={{ mb: 2, maxWidth: 500, mx: 'auto' }}
                onClose={() => setSubmitStatus({ success: false, message: "" })}
              >
                {submitStatus.message}
              </Alert>
            )}

            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrintClick}
              disabled={!canPrint || isSubmitting}
              sx={{ mt: 1 }}
            >
              Print Form
            </Button>

            {!canPrint && !isSubmitting && (
              <Typography 
                variant="caption" 
                color="error" 
                sx={{ display: 'block', mt: 1 }}
              >
                Please agree to the terms before printing
              </Typography>
            )}
          </>
        ) : (
          <>
            <Alert severity="success" sx={{ mb: 2, maxWidth: 500, mx: 'auto' }}>
              You have already agreed to the terms and conditions
            </Alert>
            
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrintClick}
            >
              Print Form
            </Button>
          </>
        )}
      </Box>

      <Box
        ref={formRef}
        sx={{
          backgroundColor: "#fff",
          p: 2,
          fontFamily: "Times New Roman, serif",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "relative",
            mb: 2,
            minHeight: "70px",
          }}
        >
          <Box sx={{ position: 'absolute', left: 0, top: 0 }}>
            <img
              src={config?.IMAGE_PATH ? config.IMAGE_PATH + 'logo512.png' : '/logo512.png'}
              alt='HLFPPT Logo'
              height="55"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/logo512.png';
              }}
            />
          </Box>

          <Typography
            align="center"
            sx={{
              fontSize: "22px",
              fontWeight: "bold",
              letterSpacing: "2px",
            }}
          >
            UNDERTAKING
          </Typography>
        </Box>

        {/* Content */}
        <Typography
          sx={{
            fontSize: "12.5px",
            lineHeight: 1.8,
            textAlign: "justify",
          }}
        >
          I, <strong>{getCandidateName()}</strong>, S/o / D/o{" "}
          <strong>{getFatherName()}</strong>, a resident of{" "}
          <strong>{getAddress()}</strong>, hereby accept the
          job/designation of <strong>{getDesignation()}</strong> offered to
          me along with all the related Terms & Conditions. I have undergone
          the complete Induction and Orientation, where I have been educated
          about the Organizational Purpose, Mission, Vision, Values, Norms,
          Code of Conduct etc., besides the Job Description related to me. I
          am ready to undertake any additional responsibilities or change in
          role in the existing pay or revised pay as per the need of the
          organization. I have understood all the mentioned terms and assure
          to abide by the organizational norms in the most sincere manner.
        </Typography>

        <Typography
          sx={{
            mt: 1,
            fontSize: "12.5px",
            lineHeight: 1.8,
            textAlign: "justify",
          }}
        >
          I also understand that my employment in the organization is
          subjected to the continuation of the Project or as per the contract
          expiry mentioned in the offer/appointment letter whichever is
          earlier and expected level of performance and conduct at workplace.
          In case of any misconduct on my part, the organization has all the
          right to initiate disciplinary action against me.
        </Typography>

        <Box sx={{ mt: 1, pl: 2 }}>
          <Typography sx={{ fontSize: "12.5px", lineHeight: 1.8 }}>
            <strong>1.</strong> That I <strong>{getCriminalRecord()}</strong>{" "}
            bear any criminal record / cases pending against me.
            {undertakeData?.criminalRecordDetails && (
              <> ({undertakeData.criminalRecordDetails})</>
            )}
          </Typography>

          <Typography sx={{ fontSize: "12.5px", lineHeight: 1.8 }}>
            <strong>2.</strong> That I shall adhere to any new rules &
            norms of the organization including POSH (Prevention of Sexual
            Harassment).
          </Typography>

          <Typography sx={{ fontSize: "12.5px", lineHeight: 1.8 }}>
            <strong>3.</strong> All documents and certificates submitted by
            me are{" "}
            <strong>
              {undertakeData?.documentsAuthentic === "yes"
                ? "authentic"
                : undertakeData?.documentsAuthentic === "no"
                ? "not authentic"
                : "authentic"}
            </strong>
            .
            {undertakeData?.documentsDetails && (
              <> ({undertakeData.documentsDetails})</>
            )}
          </Typography>

          <Typography sx={{ fontSize: "12.5px", lineHeight: 1.8 }}>
            <strong>4.</strong> That I shall not terminate my services from
            the organization before giving sufficient written notice.
          </Typography>

          <Typography sx={{ fontSize: "12.5px", lineHeight: 1.8 }}>
            <strong>5.</strong> I understand that this offer is made to me
            purely based on merit and qualification.
          </Typography>
        </Box>

        <Typography
          sx={{
            mt: 1,
            fontSize: "12.5px",
            lineHeight: 1.8,
            textAlign: "justify",
          }}
        >
          I sign this undertaking today, the <strong>{getDay()}</strong> of{" "}
          <strong>{getMonth()}</strong> <strong>{getYear()}</strong> in the
          best of my physical and mental health.
        </Typography>

        {/* Footer */}
        <Box className="footer-section" sx={{ mt: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography fontWeight="bold" fontSize="12px">
                Place:
              </Typography>

              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  minHeight: "24px",
                }}
              >
                <Typography fontSize="12px">
                  {appliedJobDetails?.job_location || appliedJobDetails?.annexure_eleven_form_data?.place_of_posting || candidateData?.applied_jobs?.find((item) => item?.job_id === candidateData?.job_id)?.proposed_location || candidateData?.location || ""}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Typography fontWeight="bold" fontSize="12px">
                Date:
              </Typography>

              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  minHeight: "24px",
                }}
              >
                <Typography fontSize="12px">
                  {formatDate(undertakeData?.date)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Typography fontWeight="bold" fontSize="12px">
                Signature of Candidate:
              </Typography>

              <Box
                sx={{
                  // borderBottom: "1px solid #000",
                  minHeight: "50px",
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                {candidateData?.applicant_form_data?.signature && (
                  <img
                    src={`${config?.IMAGE_PATH || ""}${
                      candidateData.applicant_form_data.signature
                    }`}
                    alt="candidate-signature"
                    style={{
                      maxHeight: 40,
                      objectFit: "contain",
                    }}
                  />
                )}
              </Box>

              <Typography
                sx={{
                  mt: 1,
                  fontWeight: "bold",
                  fontSize: "12px",
                }}
              >
                {getCandidateName()}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default UndertakeForm;